import React, { useState, useEffect, useRef, useImperativeHandle, useMemo } from 'react';
import ReactDOM from 'react-dom';


const ListNameEditor = (props) => {
    const {
        value,
        onValueChange,
        node,
        data,
        onOpenNewListModal,
        onListSegmentsFetched,
        getListValue,
        setlistSegmentsEditor,
        setisListNameUpdated,
        agGridRef
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState(value || '');
    const [selectedListId, setSelectedListId] = useState(null);
    
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const [searchText, setSearchText] = useState('');
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const [portalContainer, setPortalContainer] = useState(null);

    useEffect(() => {
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.zIndex = '10000';
        document.body.appendChild(container);
        setPortalContainer(container);

        return () => {
            if (container) {
                document.body.removeChild(container);
            }
        };
    }, []);

    const availableOptions = useMemo(() => {
        const options = Array.isArray(getListValue) && getListValue.length > 0
            ? getListValue.map(list => ({
                id: list.listID ?? list.listMasterID ?? list.id,
                name: list.listName,
                value: list.listName,
                dataId: list.listID ?? list.listMasterID ?? list.id
            }))
            : [];

        if (value && value !== '') {
            const exists = options.some(opt => String(opt.value) === String(value));
            if (!exists) {
                options.unshift({
                    id: null,
                    name: String(value),
                    value: String(value),
                    dataId: null,
                    __fallback: true
                });
            }
        }
        return options;
    }, [getListValue, value]);

    const filteredOptions = useMemo(() => {
        if (!searchText.trim()) return availableOptions;
        const term = searchText.trim().toLowerCase();
        return availableOptions.filter(opt =>
            String(opt.name || '').toLowerCase().includes(term)
        );
    }, [availableOptions, searchText]);

    useEffect(() => {
        if (isOpen && triggerRef.current && portalContainer) {
            const rect = triggerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const DROPDOWN_HEIGHT = 300;

            let top;
            if (spaceBelow < DROPDOWN_HEIGHT && rect.top > DROPDOWN_HEIGHT) {
                top = rect.top - DROPDOWN_HEIGHT - 5;
            } else {
                top = rect.bottom + 5;
            }

            portalContainer.style.top = `${top}px`;
            portalContainer.style.left = `${rect.left}px`;
            portalContainer.style.minWidth = `${rect.width}px`;
        }
    }, [isOpen, portalContainer]);

  

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (value !== undefined && value !== null) {
            setLocalValue(value);
            const found = availableOptions.find(opt => String(opt.value) === String(value));
            setSelectedListId(found?.id ?? null);
        }
    }, [value, availableOptions]);

    useEffect(() => {
        if (!isOpen) {
            setSearchText('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            requestAnimationFrame(() => searchInputRef.current?.focus());
        }
    }, [isOpen]);

  
    const handleSelect = async (option) => {
        if (option.value === '__new__') {
            onOpenNewListModal?.();
            setIsOpen(false);
            return;
        }

        const selectedValue = option.value;
        const listId = option.id;

        setLocalValue(selectedValue);
        setSelectedListId(listId);
        setIsOpen(false);

      
        if (node) {
            node.setDataValue('listName', selectedValue);
            node.setDataValue('listId', listId);
            node.setDataValue('listID', listId);
            node.setDataValue('listMasterID', listId);
            node.setDataValue('listSegments', []); 
            node.setDataValue('select', '');        

            if (setisListNameUpdated) {
                setisListNameUpdated(true);
            }

            node.gridApi?.refreshCells({
                rowNodes: [node],
                columns: ['listName', 'select'],
                force: true
            });
        }

       
        if (setlistSegmentsEditor) {
            setlistSegmentsEditor([]);
        }

       
        onValueChange?.(selectedValue, listId, []);
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const getDisplayValue = () => {
        if (!localValue) return 'Select a list';
        return localValue;
    };

    useImperativeHandle(agGridRef, () => ({
        getValue: () => localValue,
        getListId: () => selectedListId,
        isCancelBeforeStart: () => false,
        isCancelAfterEnd: () => false
    }), [localValue, selectedListId]);

    const renderDropdown = () => {
        if (!isOpen || !portalContainer) return null;

        return ReactDOM.createPortal(
            <div
                ref={dropdownRef}
                className="custom-select-dropdown-portal"
                style={{
                    position: 'fixed',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    maxHeight: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 10000
                }}
            >
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search list..."
                        style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                            outline: 'none'
                        }}
                    />
                </div>

                <div className="custom-select-options-wrapper" style={{ flex: 1, overflowY: 'auto', maxHeight: '250px' }}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div
                                key={option.dataId ?? option.value}
                                className={`custom-select-option ${localValue === option.value ? 'selected' : ''}`}
                                onClick={() => handleSelect(option)}
                                style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#333',
                                    margin: '2px 4px',
                                    borderRadius: '2px',
                                    backgroundColor: localValue === option.value ? '#e3f2fd' : 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = localValue === option.value ? '#e3f2fd' : 'transparent';
                                }}
                            >
                                {option.name}
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '10px 12px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
                            No lists found
                        </div>
                    )}
                </div>

                <div
                    className="custom-select-option new-list-option"
                    onClick={() => handleSelect({ value: '__new__', id: null, name: '+ Create new list' })}
                    style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#2e6f9f',
                        fontWeight: 'bold',
                        margin: '2px 4px',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e3f2fd';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                    }}
                >
                    + Create new list
                </div>
            </div>,
            portalContainer
        );
    };

    return (
        <>
            <div className="custom-select-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div
                    ref={triggerRef}
                    className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
                    onClick={handleOpen}
                    style={{
                        width: '100%',
                        height: '100%',
                        padding: '4px 8px',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box'
                    }}
                >
                    <span style={{ flex: 1, color: '#333' }}>{getDisplayValue()}</span>
                    <span style={{ fontSize: '10px', color: '#999', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                </div>
            </div>
            {renderDropdown()}
        </>
    );
};

export default ListNameEditor;